import { Injectable } from "@angular/core";
import { PaymentGroup, PaymentsGroupsFromServer, GroupByIdFromServer, PaymentTerminalItem, PaymentServiceItem } from "./payment.model";

import { UtilsService } from "../utils/utils.service";
import { Observable, Observer } from "rxjs";
import { ApiService } from '../api/api.service';
import {map} from "rxjs/operators";

@Injectable()
export class PaymentsService {
  private paymentsGroups: PaymentGroup[];
  private paymentsGroupsItems: PaymentTerminalItem[];

  constructor(private apiService: ApiService, private utils: UtilsService) {}

  private loadPaymentGroups(): Observable<PaymentsGroupsFromServer> {
    return this.apiService.get('rest/personal/payment_services/search');
  }

  getPaymentGroups(): Observable<PaymentGroup[]> {
    return Observable.create((observer: Observer<PaymentGroup[]>) => {
      if (this.paymentsGroups) {
        observer.next(this.paymentsGroups);
        observer.complete();
        return;
      }
      this.loadPaymentGroups().subscribe(res => {
        this.paymentsGroups = this.prepareGroups(this.utils.getArray(res ? res.groups || [] : []));
        observer.next(this.paymentsGroups);
        observer.complete();
        return;
      });
    });
  }

  prepareGroups(groups: PaymentGroup[]): PaymentGroup[] {
    return groups.map(g => {
      g.name = g.name.replace(/\//g, ' / ');
      return g;
    });
  }

  getPaymentImage(link: string): string {
    return link;
  }

  getAllGroups(): Observable<PaymentTerminalItem[]> {
    return Observable.create((observer: Observer<PaymentTerminalItem[]>) => {
      if (this.paymentsGroupsItems) {
        observer.next(this.paymentsGroupsItems);
        observer.complete();
        return;
      }
      this.loadPaymentGroups().subscribe(res => {
        this.paymentsGroups = this.utils.getArray(res.groups || []);
        this.paymentsGroupsItems = this.createGroupsItems(this.paymentsGroups);
        observer.next(this.paymentsGroupsItems);
        observer.complete();
        return;
      });
    });
  }

  getGroupById(id: string): Observable<PaymentTerminalItem[]> {
    return Observable.create((observer: Observer<PaymentTerminalItem[]>) => {
      this.apiService
        .get(`rest/personal/payment_services_our/search?group_id=${id}`)
        .subscribe((res: any) => {
          const servicesWithGroups = this.utils.getArray(res.paymentGroupsAndServicesList);
          const services = servicesWithGroups.filter(v => v['@type'] === 'paymentServiceMetinv');
          const groups = servicesWithGroups.filter(v => v['@type'] === 'paymentServicesGroupMetinv')
          observer.next([
            ...this.createGroupsItems(groups),
            ...this.createServiceItems(services)
          ]);
          observer.complete();
        });
    });
  }

  getDataFromQRCode(base64QR: string): Observable<any> {
    return this.apiService.post('rest/personal/qr/base64', base64QR).pipe(
      map((res: any) => {
        if (res && res.errorCode) {
          throw res
        }
        return res;
      })
    )
  }

  searchPayments(term: string, group?: string): Observable<PaymentTerminalItem[]> {
    return Observable.create((observer: Observer<PaymentTerminalItem[]>) => {
      const searchTerm = `text=${encodeURIComponent(term.trim())}`;
      const searchGroup = group ? `group_id=${group}&` : '';
      this.apiService
        .get(`rest/personal/payment_services/search?${searchGroup}${searchTerm}`)
        .subscribe((res: GroupByIdFromServer) => {
          observer.next([
            ...this.createGroupsItems(this.utils.getArray(res.groups)),
            ...this.createServiceItems(this.utils.getArray(res.paymentServices))
          ]);
          observer.complete();
        });
    });
  }

  createServiceItems(services: PaymentServiceItem[]): PaymentTerminalItem[] {
    return services.map((service: PaymentServiceItem) => ({
      title: service.name,
      image: this.getPaymentImage(service.image),
      type: 'service',
      id: service.id,
      serviceObject: service
    }));
  }

  createGroupsItems(groups: PaymentGroup[]): PaymentTerminalItem[] {
    return groups.map((group: PaymentGroup) => ({
      title: group.name,
      image: this.getPaymentImage(group.image),
      type: 'group',
      id: group.id
    }));
  }

}
